'use client';

import { useEffect } from 'react';
import Image from 'next/image';

const TUTORIAL_IMAGE_SIZES = '(max-width: 1200px) 100vw, 1200px';
/** 표시 최대 크기(1200px) 기준 — 로더/프록시가 이 크기로 요청해 과다 다운로드 방지 */
const TUTORIAL_DIMS: Record<string, { width: number; height: number }> = {
  tutorial2: { width: 1200, height: 675 },
  tutorial3: { width: 1200, height: 675 },
  tutorial4: { width: 1200, height: 675 },
  tutorial5: { width: 1200, height: 675 },
  tutorial6: { width: 1200, height: 675 },
  tutorial7: { width: 1200, height: 675 },
  tutorial8: { width: 1200, height: 675 },
  tutorial9: { width: 1200, height: 675 },
  tutorial10: { width: 1200, height: 675 },
  tutorial11: { width: 1200, height: 675 },
};

function TutorialClient() {
  useEffect(() => {
    document.body.classList.add('tutorial-page-active');
    document.documentElement.classList.add('tutorial-page-active');

    const tocLinks = document.querySelectorAll('.toc-link');
    const sections = document.querySelectorAll('section[id]');
    const sidebar = document.querySelector('.tutorial-sidebar');
    let scrollPosition = 0;
    let isHovering = false;
    let isNavigating = false;

    function updateActiveTocLink() {
      if (isNavigating) return;

      // support 섹션 먼저 체크 - threshold를 더 크게 설정
      const supportSection = document.getElementById('support');
      if (supportSection) {
        const supportTop = supportSection.offsetTop - 800; // 800px 전부터 감지
        if (window.pageYOffset >= supportTop) {
          // support 섹션에 있으면 모든 하이라이팅 제거하고 해시 제거
          tocLinks.forEach(link => {
            link.classList.remove('active');
          });
          if (window.location.hash) {
            window.history.replaceState(null, '', window.location.pathname);
          }
          return;
        }
      }

      // 다른 섹션들 체크 (support 제외)
      let current = '';
      sections.forEach(section => {
        const sectionId = section.getAttribute('id');
        if (sectionId && sectionId !== 'support' && window.pageYOffset >= (section as HTMLElement).offsetTop - 200) {
          current = sectionId;
        }
      });

      // URL 해시 업데이트
      const newHash = current ? `#${current}` : '';
      if (window.location.hash !== newHash) {
        window.history.replaceState(null, '', newHash || window.location.pathname);
      }

      // 하이라이팅 업데이트
      tocLinks.forEach(link => {
        link.classList.remove('active');
        if (current && link.getAttribute('href') === `#${current}`) {
          link.classList.add('active');
        }
      });
    }

    function handleMouseEnter() {
      isHovering = true;
      scrollPosition = window.scrollY;
    }

    function handleMouseLeave() {
      isHovering = false;
      setTimeout(() => {
        if (!isHovering && !isNavigating) {
          window.scrollTo({ top: scrollPosition, behavior: 'instant' as ScrollBehavior });
        }
        isNavigating = false; // 리셋
      }, 300);
    }

    window.addEventListener('scroll', updateActiveTocLink);
    updateActiveTocLink();
    sidebar?.addEventListener('mouseenter', handleMouseEnter);
    sidebar?.addEventListener('mouseleave', handleMouseLeave);

    // 링크 클릭 시 네비게이션 플래그 설정
    tocLinks.forEach(link => {
      link.addEventListener('click', () => {
        isNavigating = true;
        // 스크롤 애니메이션이 완료될 때까지 충분한 시간 대기 (1초)
        setTimeout(() => {
          isNavigating = false;
        }, 1000);
      });
    });

    return () => {
      document.body.classList.remove('tutorial-page-active');
      document.documentElement.classList.remove('tutorial-page-active');
      window.removeEventListener('scroll', updateActiveTocLink);
      sidebar?.removeEventListener('mouseenter', handleMouseEnter);
      sidebar?.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <>
      {/* Step 2 */}
      <section className='flex min-h-screen flex-col justify-center gap-10 bg-background px-6 py-16 sm:px-10 sm:py-20 scroll-snap-start scroll-snap-always'>
        <div className='mx-auto w-full max-w-[1200px]'>
          <Image
            src='/tutorial2.png'
            alt='Screenshot of the sign-up screen for creating a new Tindlo account.'
            width={TUTORIAL_DIMS.tutorial2.width}
            height={TUTORIAL_DIMS.tutorial2.height}
            sizes={TUTORIAL_IMAGE_SIZES}
            className='block w-full rounded-(--radius) m-0'
          />
          <p className='mt-10 max-w-[900px] m-0 text-[clamp(1rem,2vw,1.25rem)] leading-[1.7] text-[hsl(var(--text-secondary))]'>
            Sign up for a new account.
          </p>
        </div>
      </section>

      {/* Step 3 */}
      <section className='flex min-h-screen flex-col justify-center gap-10 bg-background px-6 py-16 sm:px-10 sm:py-20 scroll-snap-start scroll-snap-always'>
        <div className='mx-auto w-full max-w-[1200px]'>
          <Image
            src='/tutorial3.png'
            alt='Screenshot of the screen where you create your organization in Tindlo.'
            width={TUTORIAL_DIMS.tutorial3.width}
            height={TUTORIAL_DIMS.tutorial3.height}
            sizes={TUTORIAL_IMAGE_SIZES}
            className='block w-full rounded-(--radius) m-0'
          />
          <p className='mt-10 max-w-[900px] m-0 text-[clamp(1rem,2vw,1.25rem)] leading-[1.7] text-[hsl(var(--text-secondary))]'>
            Create your organization.
          </p>
        </div>
      </section>

      {/* Step 4 - Dayview */}
      <section
        id='Dayview'
        className='flex min-h-screen flex-col justify-center gap-10 bg-background px-6 py-16 sm:px-10 sm:py-20 scroll-snap-start scroll-snap-always'
      >
        <div className='mx-auto w-full max-w-[1200px]'>
          <Image
            src='/tutorial4.png'
            alt='Screenshot of the Tindlo Dayview welcome screen after signing in.'
            width={TUTORIAL_DIMS.tutorial4.width}
            height={TUTORIAL_DIMS.tutorial4.height}
            sizes={TUTORIAL_IMAGE_SIZES}
            className='block w-full rounded-(--radius) m-0'
          />
          <h2 className='mt-10 max-w-[900px] m-0 font-display text-[clamp(1.5rem,3vw,2.25rem)] font-bold leading-[1.3] text-foreground'>
            Welcome to Tindlo
          </h2>
        </div>
      </section>

      {/* Step 5 */}
      <section className='flex min-h-screen flex-col justify-center gap-10 bg-background px-6 py-16 sm:px-10 sm:py-20 scroll-snap-start scroll-snap-always'>
        <div className='mx-auto w-full max-w-[1200px]'>
          <Image
            src='/tutorial5.png'
            alt='Screenshot of the Tindlo main dashboard where you start projects and collaborate with your team.'
            width={TUTORIAL_DIMS.tutorial5.width}
            height={TUTORIAL_DIMS.tutorial5.height}
            sizes={TUTORIAL_IMAGE_SIZES}
            className='block w-full rounded-(--radius) m-0'
          />
          <p className='mt-10 max-w-[900px] m-0 font-display text-[clamp(1.25rem,2.5vw,1.75rem)] font-bold text-foreground'>
            Your magic begins here
          </p>
          <p className='max-w-[900px] m-0 mt-4 text-[clamp(1rem,1.8vw,1.2rem)] leading-[1.7] text-[hsl(var(--text-secondary))]'>
            Create projects, collaborate with your teammates, and build something amazing together.
          </p>
          <p className='max-w-[900px] m-0 mt-4 text-[clamp(0.95rem,1.6vw,1.1rem)] leading-[1.7] text-muted-foreground'>
            Note: File uploads are <strong className='font-semibold text-accent'>limited</strong> on the free plan.
          </p>
        </div>
      </section>

      {/* Step 6 */}
      <section className='flex min-h-screen flex-col justify-center gap-10 bg-background px-6 py-16 sm:px-10 sm:py-20 scroll-snap-start scroll-snap-always'>
        <div className='mx-auto w-full max-w-[1200px]'>
          <Image
            src='/tutorial6.png'
            alt='Screenshot showing the submenu to explore each Tindlo feature.'
            width={TUTORIAL_DIMS.tutorial6.width}
            height={TUTORIAL_DIMS.tutorial6.height}
            sizes={TUTORIAL_IMAGE_SIZES}
            className='block w-full rounded-(--radius) m-0'
          />
          <h2 className='mt-10 max-w-[900px] m-0 font-display text-[clamp(1.5rem,3vw,2.25rem)] font-bold leading-[1.3] text-foreground'>
            Explore the submenu
          </h2>
          <p className='max-w-[900px] m-0 mt-4 text-[clamp(1rem,1.8vw,1.2rem)] leading-[1.7] text-[hsl(var(--text-secondary))]'>
            Try out each feature and discover what fits your workflow best.
          </p>
        </div>
      </section>

      {/* Step 7 - Roadmap */}
      <section
        id='Roadmap'
        className='flex min-h-screen flex-col justify-center gap-10 bg-background px-6 py-16 sm:px-10 sm:py-20 scroll-snap-start scroll-snap-always'
      >
        <div className='mx-auto w-full max-w-[1200px]'>
          <Image
            src='/tutorial7.png'
            alt='Screenshot of the Roadmap view in Tindlo for building timelines and viewing your project schedule.'
            width={TUTORIAL_DIMS.tutorial7.width}
            height={TUTORIAL_DIMS.tutorial7.height}
            sizes={TUTORIAL_IMAGE_SIZES}
            className='block w-full rounded-(--radius) m-0'
          />
          <h2 className='mt-10 max-w-[900px] m-0 font-display text-[clamp(1.5rem,3vw,2.25rem)] font-bold leading-[1.3] text-foreground'>
            Build your timeline
          </h2>
          <p className='max-w-[900px] m-0 mt-4 text-[clamp(1rem,1.8vw,1.2rem)] leading-[1.7] text-[hsl(var(--text-secondary))]'>
            Use the Roadmap view to set timelines and understand your project&apos;s full schedule at a glance.
          </p>
        </div>
      </section>

      {/* Step 8 - FileFlow */}
      <section
        id='FileFlow'
        className='flex min-h-screen flex-col justify-center gap-10 bg-background px-6 py-16 sm:px-10 sm:py-20 scroll-snap-start scroll-snap-always'
      >
        <div className='mx-auto w-full max-w-[1200px]'>
          <Image
            src='/tutorial8.png'
            alt='Screenshot of File Flow in Tindlo where documents linked to tasks are gathered in one place.'
            width={TUTORIAL_DIMS.tutorial8.width}
            height={TUTORIAL_DIMS.tutorial8.height}
            sizes={TUTORIAL_IMAGE_SIZES}
            className='block w-full rounded-(--radius) m-0'
          />
          <h2 className='mt-10 max-w-[900px] m-0 font-display text-[clamp(1.5rem,3vw,2.25rem)] font-bold leading-[1.3] text-foreground'>
            Enjoy File Flow
          </h2>
          <p className='max-w-[900px] m-0 mt-4 text-[clamp(1rem,1.8vw,1.2rem)] leading-[1.7] text-[hsl(var(--text-secondary))]'>
            Quickly gather and view the latest documents linked across your tasks—no more searching through scattered
            tabs or messages.
          </p>
        </div>
      </section>

      {/* Step 9 - Kanban */}
      <section
        id='Kanban'
        className='flex min-h-screen flex-col justify-center gap-10 bg-background px-6 py-16 sm:px-10 sm:py-20 scroll-snap-start scroll-snap-always'
      >
        <div className='mx-auto w-full max-w-[1200px]'>
          <Image
            src='/tutorial9.png'
            alt='Screenshot of the New Kanban board in Tindlo for managing tasks visually by columns.'
            width={TUTORIAL_DIMS.tutorial9.width}
            height={TUTORIAL_DIMS.tutorial9.height}
            sizes={TUTORIAL_IMAGE_SIZES}
            className='block w-full rounded-(--radius) m-0'
          />
          <h2 className='mt-10 max-w-[900px] m-0 font-display text-[clamp(1.5rem,3vw,2.25rem)] font-bold leading-[1.3] text-foreground'>
            New Kanban
          </h2>
          <p className='max-w-[900px] m-0 mt-4 text-[clamp(1rem,1.8vw,1.2rem)] leading-[1.7] text-[hsl(var(--text-secondary))]'>
            New Kanban helps you separate tasks by purpose and manage your work visually and intuitively, keeping
            complex projects simple at a glance.
          </p>
        </div>
      </section>

      {/* Step 10 - Branch */}
      <section
        id='Branch'
        className='flex min-h-screen flex-col justify-center gap-10 bg-background px-6 py-16 sm:px-10 sm:py-20 scroll-snap-start scroll-snap-always'
      >
        <div className='mx-auto w-full max-w-[1200px]'>
          <Image
            src='/tutorial10.png'
            alt='Screenshot of Branch in Tindlo for creating outlines and documents in one organized space.'
            width={TUTORIAL_DIMS.tutorial10.width}
            height={TUTORIAL_DIMS.tutorial10.height}
            sizes={TUTORIAL_IMAGE_SIZES}
            className='block w-full rounded-(--radius) m-0'
          />
          <h2 className='mt-10 max-w-[900px] m-0 font-display text-[clamp(1.5rem,3vw,2.25rem)] font-bold leading-[1.3] text-foreground'>
            Branch
          </h2>
          <p className='max-w-[900px] m-0 mt-4 text-[clamp(1rem,1.8vw,1.2rem)] leading-[1.7] text-[hsl(var(--text-secondary))]'>
            Branch lets you create{' '}
            <strong className='font-semibold text-accent'>both structured outlines and full documents</strong>, so you
            can store any type of project material in one organized space.
          </p>
        </div>
      </section>

      {/* Step 11 - Setting */}
      <section
        id='Setting'
        className='flex min-h-screen flex-col justify-center gap-10 bg-background px-6 py-16 sm:px-10 sm:py-20 scroll-snap-start scroll-snap-always'
      >
        <div className='mx-auto w-full max-w-[1200px]'>
          <Image
            src='/tutorial11.png'
            alt='Screenshot of Settings in Tindlo where you generate an invite link to add teammates.'
            width={TUTORIAL_DIMS.tutorial11.width}
            height={TUTORIAL_DIMS.tutorial11.height}
            sizes={TUTORIAL_IMAGE_SIZES}
            className='block w-full rounded-(--radius) m-0'
          />
          <h2 className='mt-10 max-w-[900px] m-0 font-display text-[clamp(1.5rem,3vw,2.25rem)] font-bold leading-[1.3] text-foreground'>
            Invitation code
          </h2>
          <p className='max-w-[900px] m-0 mt-4 text-[clamp(1rem,1.8vw,1.2rem)] leading-[1.7] text-[hsl(var(--text-secondary))]'>
            In Settings, click &quot;Generate Link&quot; to create an invite code. Share it with your teammates to
            invite them.
          </p>
          <p className='max-w-[900px] m-0 mt-4 text-[clamp(0.95rem,1.6vw,1.1rem)] text-muted-foreground'>
            Note: The free plan allows up to 20 members per organization.
          </p>
        </div>
      </section>

      {/* Calendar Integration */}
      <section className='flex min-h-screen flex-col justify-center bg-background px-6 py-12 sm:px-10 sm:py-16 scroll-snap-start scroll-snap-always'>
        <div className='mx-auto w-full max-w-[900px]'>
          <h2 className='font-display text-[clamp(1.5rem,3vw,2.25rem)] font-bold leading-[1.3] text-foreground'>
            Calendar integration
          </h2>
          <p className='mt-4 text-[clamp(0.95rem,1.8vw,1.2rem)] leading-[1.7] text-[hsl(var(--text-secondary))]'>
            Import your Google Calendar in Settings. Events import smoothly; syncing back to Google isn&apos;t available
            yet. <span className='text-muted-foreground'>(Coming soon)</span>
          </p>
        </div>
      </section>

      {/* Customize Your Workday */}
      <section className='flex min-h-screen flex-col justify-center px-6 py-12 sm:px-10 sm:py-16 scroll-snap-start scroll-snap-always'>
        <div className='mx-auto w-full max-w-[900px]'>
          <h2 className='font-display text-[clamp(1.5rem,3vw,2.25rem)] font-bold leading-[1.3] text-foreground'>
            Customize your workday
          </h2>
          <p className='mt-4 text-[clamp(0.95rem,1.8vw,1.2rem)] leading-[1.7] text-[hsl(var(--text-secondary))]'>
            Set your preferred work schedule and manage your tasks more efficiently.
          </p>
        </div>
      </section>

      {/* Contact & Support */}
      <section
        id='support'
        className='flex flex-col items-center justify-center px-6 py-16 sm:py-24 text-center'
        style={{ background: 'hsl(var(--muted) / 0.3)' }}
      >
        <h2 className='mb-8 font-display text-[clamp(1.5rem,3vw,2.25rem)] font-bold text-foreground'>
          Contact & Support
        </h2>
        <a
          href='mailto:hello@tindlo.com'
          className='text-[hsl(var(--text-secondary))] hover:text-foreground transition-colors text-[clamp(1rem,2vw,1.25rem)]'
        >
          hello@tindlo.com
        </a>
      </section>
    </>
  );
}

export default TutorialClient;
